// WebCodecs decode+encode proof-of-concept + micro-benchmark (Local Engine Phase 1).
// Runs a real round-trip (generate frames -> VideoEncoder -> EncodedVideoChunk ->
// VideoDecoder) to MEASURE that WebCodecs actually works and how fast, rather than
// trusting API presence. Never runs during a normal export.

async function pickEncoderConfig(width, height) {
  const bases = [
    { name: "h264", codec: "avc1.42001f" },
    { name: "vp9", codec: "vp09.00.10.08" },
  ];
  for (const b of bases) {
    const cfg = { codec: b.codec, width, height, bitrate: 4_000_000, framerate: 30 };
    for (const hw of ["prefer-hardware", "prefer-software", "no-preference"]) {
      try {
        const s = await window.VideoEncoder.isConfigSupported({ ...cfg, hardwareAcceleration: hw });
        if (s?.supported) return { config: s.config || { ...cfg, hardwareAcceleration: hw }, name: b.name, hw };
      } catch { /* try next */ }
    }
  }
  return null;
}

export async function runWebCodecsPoc({ width = 640, height = 360, frames = 60 } = {}) {
  const t0 = performance.now();
  if (typeof window === "undefined" || !("VideoEncoder" in window) || !("VideoDecoder" in window)) {
    return { ok: false, reason: "WebCodecs not available in this browser." };
  }
  let picked;
  try {
    picked = await pickEncoderConfig(width, height);
  } catch (e) {
    return { ok: false, reason: "Encoder probe failed: " + (e?.message || e) };
  }
  if (!picked) return { ok: false, reason: "No supported WebCodecs encoder configuration on this device." };

  const canvas = ("OffscreenCanvas" in window) ? new OffscreenCanvas(width, height) : document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");

  const chunks = [];
  let encoded = 0;
  let decoded = 0;

  try {
    const encoder = new window.VideoEncoder({
      output: (chunk) => { chunks.push(chunk); encoded++; },
      error: () => {},
    });
    encoder.configure(picked.config);

    const encStart = performance.now();
    for (let i = 0; i < frames; i++) {
      ctx.fillStyle = `hsl(${(i * 6) % 360},70%,45%)`;
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#fff";
      ctx.font = "40px sans-serif";
      ctx.fillText("CM " + i, 20 + (i % 20), height / 2);
      const frame = new window.VideoFrame(canvas, { timestamp: (i * 1_000_000) / 30, duration: 1_000_000 / 30 });
      encoder.encode(frame, { keyFrame: i % 30 === 0 });
      frame.close();
    }
    await encoder.flush();
    const encMs = performance.now() - encStart;
    encoder.close();

    // Decode round-trip
    const decStart = performance.now();
    let decErr = null;
    const decoder = new window.VideoDecoder({
      output: (f) => { decoded++; f.close(); },
      error: (e) => { decErr = e; },
    });
    // derive a decoder config from the encoder codec
    decoder.configure({ codec: picked.config.codec, codedWidth: width, codedHeight: height });
    for (const c of chunks) {
      try { decoder.decode(c); } catch { /* skip */ }
    }
    try { await decoder.flush(); } catch { /* ignore */ }
    const decMs = performance.now() - decStart;
    decoder.close();

    const encodeFps = encoded ? Math.round((encoded / encMs) * 1000) : 0;
    const decodeFps = decoded ? Math.round((decoded / decMs) * 1000) : 0;
    return {
      ok: encoded > 0,
      codec: picked.name,
      hardware: picked.hw === "prefer-hardware",
      accelerationPref: picked.hw,
      framesEncoded: encoded,
      framesDecoded: decoded,
      encodeFps,
      decodeFps,
      realtimeFactor: Math.round((encodeFps / 30) * 100) / 100,
      totalMs: Math.round(performance.now() - t0),
      decodeError: decErr ? String(decErr.message || decErr) : null,
      reason: encoded > 0 ? "WebCodecs encode/decode round-trip succeeded." : "Encoder produced no chunks.",
    };
  } catch (e) {
    return { ok: false, reason: "WebCodecs round-trip failed: " + (e?.message || e) };
  }
}
